import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { AlertCircle, CreditCard, RefreshCw, Lock, Plus, WalletCards } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { SavedCard } from '../data/mockSavedCards';
import { useSelector } from 'react-redux';
import { getDecodeId } from 'slices/userProfile/thunk';
import { CallAPIGetPromise, CallAPIPostPromise } from 'Common/commaon';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { selectUserDetails } from 'slices/userProfile/reducer';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import decodeJwt from 'helpers/jwt-token-access/jwtdecode';
import api from 'Common/api';
import './lead-payment.css';

declare const EmbeddedPayments: any;

interface PaymentDeclinedModalProps {
  lead: any;
  onClose: () => void;
  onRetry: () => void;
  paymentProvider:any;
  rejectedCardReson:any;
  setRejectedReason:any;
  mode?: 'checkout' | 'retry';
}

type LeadPaymentMethod = 'wallet' | 'saved-card' | 'new-card';

function useWalletBalance() {
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get('/v2/api/wallet/me')
      .then((response: any) => {
        if (active) setWalletBalance(Number(response?.data?.wallet?.balance || 0));
      })
      .catch(() => {
        if (active) setWalletBalance(0);
      })
      .finally(() => {
        if (active) setWalletLoading(false);
      });
    return () => { active = false; };
  }, []);

  return { walletBalance, walletLoading };
}

function WalletPaymentOption({ balance, amount, loading }: { balance: number; amount: number; loading: boolean }) {
  const sufficient = balance >= amount;
  return (
    <div className={`rounded-xl border p-4 ${sufficient ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="flex items-center gap-3">
        <span className={`rounded-xl p-3 ${sufficient ? 'bg-emerald-600' : 'bg-amber-500'} text-white`}>
          <WalletCards className="size-5" />
        </span>
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-600">Available wallet balance</div>
          <div className={`mt-1 font-semibold text-gray-950 ${loading ? 'text-xl' : 'text-2xl'}`}>
            {loading ? 'Loading…' : `$${balance.toFixed(2)}`}
          </div>
        </div>
      </div>
      {!loading && !sufficient && (
        <p className="mb-0 mt-3 text-sm text-amber-800">
          Wallet needs ${(amount - balance).toFixed(2)} more. Please use a saved or new card.
        </p>
      )}
      {!loading && sufficient && (
        <p className="mb-0 mt-3 text-sm text-emerald-800">
          ${amount.toFixed(2)} will be deducted from your wallet.
        </p>
      )}
    </div>
  );
}

export function PaymentDeclinedModal(props: PaymentDeclinedModalProps) {
  const provider = String(props.paymentProvider || '').toLowerCase();

  if (provider === 'deluxe') {
    return <DeluxePaymentDeclinedModal {...props} />;
  }

  return <StripePaymentDeclinedModal {...props} />;
}

function DeluxePaymentDeclinedModal({
  lead,
  onClose,
  rejectedCardReson,
  setRejectedReason,
  mode = 'retry',
}: PaymentDeclinedModalProps) {
  const [paymentMethod, setPaymentMethod] = useState<LeadPaymentMethod>('wallet');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedDeluxeCardId, setSelectedDeluxeCardId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [deluxeSavedCards, setDeluxeSavedCards] = useState<any[]>([]);
  const [deluxeJwt, setDeluxeJwt] = useState<string | null>(null);
  const [sdkRendered, setSdkRendered] = useState(false);
  const initializedRef = useRef(false);
  const tokenSuccessCalledRef = useRef(false);
  const userDetails = useSelector(selectUserDetails);
  const currentUser = Array.isArray(userDetails) ? userDetails[0] : userDetails;
  const isContractor = String(currentUser?.user_type || currentUser?.userType || '').toLowerCase() === 'contractor';
  const navigation = useNavigate();
  const userEmail = userDetails?.[0]?.user_email || userDetails?.user_email || '';
  const acceptanceFee = Number(lead.tradesmens_commission);
  const { walletBalance, walletLoading } = useWalletBalance();

  const resetDeluxeSdk = () => {
    setSdkRendered(false);
    setDeluxeJwt(null);
    initializedRef.current = false;
    tokenSuccessCalledRef.current = false;

    const container = document.getElementById('embeddedpayments');
    if (container) container.innerHTML = '';
  };

  const handlePaymentAccepted = async ({
    deluxeToken = '',
    deluxePaymentMethodId = '',
    expiry,
    useWallet = false,
  }: {
    deluxeToken?: string;
    deluxePaymentMethodId?: any;
    expiry?: any;
    useWallet?: boolean;
  }) => {
    const loading = toast.loading('Loading');

    try {
      await CallAPIPostPromise(`/jobs/acceptJob`, {
        projectStatus: "ACCEPTED",
        contractorId: getDecodeId(),
        jobId: Number(lead.id),
        category: lead.lead_sub_cat,
        ...(useWallet ? { paymentSource: 'wallet' } : { paymentSource: 'card' }),
        ...(deluxePaymentMethodId ? { deluxePaymentMethodId } : {}),
        ...(deluxeToken ? { deluxePaymentToken: deluxeToken, expiry } : {}),
      });

      toast.success('Lead Applied Successfully! 🎉', {
        description: `You've applied for "${lead.title}".`
      });
      onClose();
      navigation("/project-ongoing-leads");
    } catch (e: any) {
      const reason = e.reason || e.message || 'Payment could not be completed';
      setRejectedReason(reason);
      setMessage(reason);
      toast.error(e.message, {
        description: e.reason
      });
    } finally {
      setIsProcessing(false);
      toast.dismiss(loading);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (paymentMethod === 'wallet') {
      if (walletLoading) {
        setMessage('Wallet balance is still loading.');
        return;
      }
      if (walletBalance < acceptanceFee) {
        setMessage('Your wallet balance is not enough for this lead.');
        return;
      }
      setIsProcessing(true);
      await handlePaymentAccepted({ useWallet: true });
      return;
    }

    if (paymentMethod === 'saved-card') {
      if (!selectedDeluxeCardId) {
        setMessage('Please select a saved card to continue.');
        return;
      }

      setIsProcessing(true);
      await handlePaymentAccepted({
        deluxePaymentMethodId: selectedDeluxeCardId,
      });
      return;
    }

    if (!deluxeJwt) {
      setMessage('Card form is still loading. Please wait a moment.');
      return;
    }

    if (typeof EmbeddedPayments === 'undefined') {
      setMessage('Unable to load Deluxe payment form. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);
    tokenSuccessCalledRef.current = false;
    EmbeddedPayments.pay(deluxeJwt);
  };

  useEffect(() => {
    if (!isContractor) {
      return;
    }
    const decodedToken = decodeJwt();
    CallAPIPostPromise(`/v2/api/deluxe/getSavedCards/`, {
      email: decodedToken?.email || "",
      userId: getDecodeId()
    }).then((resp: any) => {
      const cards = resp?.cards || [];
      setDeluxeSavedCards(cards);
      if (cards?.length) {
        setSelectedDeluxeCardId(cards[0]?.paymentMethodId || '');
      }

      if (!cards?.length && paymentMethod === 'saved-card') {
        setPaymentMethod('new-card');
      }
    }).catch(() => {});
  }, [paymentMethod, isContractor]);

  useEffect(() => {
    if (paymentMethod !== 'new-card') {
      return;
    }

    resetDeluxeSdk();
  }, [paymentMethod]);

  useEffect(() => {
    if (paymentMethod !== 'new-card') {
      return;
    }

    const fetchJwt = async () => {
      try {
        const resp = await api.post(`/v2/api/deluxe/create-jwt`, {
          userId: getDecodeId(),
          email: userEmail,
        }) as any;

        if (!resp?.jwt) {
          throw new Error('JWT missing');
        }
        setDeluxeJwt(resp.jwt);
      } catch (error) {
        console.error('Deluxe JWT error', error);
        setMessage('Unable to initialize card form. Please try again.');
      }
    };

    fetchJwt();
  }, [paymentMethod, userEmail]);

  useEffect(() => {
    if (paymentMethod !== 'new-card' || !deluxeJwt) {
      return;
    }

    if (typeof EmbeddedPayments === 'undefined') {
      setMessage('Deluxe payment SDK is not loaded yet.');
      return;
    }

    const container = document.getElementById('embeddedpayments');
    if (!container || initializedRef.current) {
      return;
    }

    initializedRef.current = true;
    tokenSuccessCalledRef.current = false;

    EmbeddedPayments.init(deluxeJwt, {
      countryCode: "US",
      currencyCode: "USD",
      paymentMethods: ["cc"],
      collectBillingAddress: false,
      collectShippingAddress: false,
      requireBillingAddress: false,
      skipSellerStatusCheck: true,
      skipCompleteAuthorization: true,
    })
      .then((instance: any) => {
        instance
          .setEventHandlers({
            onTxnSuccess: () => {},
            onTxnFailed: () => {
              setIsProcessing(false);
            },
            onTxnAuthorized: () => {},
            onValidationError: () => {
              setMessage('Please check your card details and try again.');
              setIsProcessing(false);
            },
            onCancel: () => {
              setIsProcessing(false);
            },
            onTokenSuccess: async (_gateway: string, data: any) => {
              if (tokenSuccessCalledRef.current) return;
              tokenSuccessCalledRef.current = true;

              const token = data?.Token || data?.token;
              const expiry = data?.ExpMonthYear || data?.expiry;

              if (!token) {
                tokenSuccessCalledRef.current = false;
                setMessage('Token not received properly. Try again.');
                setIsProcessing(false);
                return;
              }

              await handlePaymentAccepted({
                deluxeToken: token,
                expiry,
              });
            },
            onTokenFailed: () => {
              tokenSuccessCalledRef.current = false;
              setMessage('Unable to tokenize card. Please try again.');
              setIsProcessing(false);
            },
          })
          .render({
            containerId: "embeddedpayments",
            paymentpanelstyle: "light",
            paybuttoncolor: "#2563eb",
            cancelbuttoncolor: "#ef4444",
          });

        setTimeout(() => {
          setSdkRendered(true);
        }, 150);
      })
      .catch((error: any) => {
        console.error('EmbeddedPayments init error:', error);
        setMessage('Unable to load card form. Please refresh and try again.');
        setSdkRendered(false);
        setIsProcessing(false);
      });
  }, [deluxeJwt, paymentMethod]);

  useEffect(() => {
    return () => {
      resetDeluxeSdk();
    };
  }, []);

  return (
    <div className=''>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent style={{
          maxHeight:'95%',
          maxWidth:window.innerHeight>600? 700:400
        }} className="lead-payment-dialog tailwind-prefix overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${mode === 'retry' ? 'text-red-600' : 'text-gray-950'}`}>
              {mode === 'retry' ? <AlertCircle className="size-5" /> : <Lock className="size-5 text-blue-600" />}
              {mode === 'retry' ? 'Payment Declined' : 'Choose payment method'}
            </DialogTitle>
            <div className='text-gray-500'>
              {mode === 'retry' ? rejectedCardReson : 'Select how you want to pay before accepting this lead.'}
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-2">
              {mode === 'retry' && rejectedCardReson && (
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertDescription>
                    <strong>Reason:</strong> {rejectedCardReson}
                  </AlertDescription>
                </Alert>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-900">Amount to Pay</span>
                  <span className="text-2xl text-blue-900">${acceptanceFee.toFixed(2)}</span>
                </div>
              </div>

              <Tabs value={paymentMethod} onValueChange={(value) => setPaymentMethod(value as LeadPaymentMethod)}>
                <TabsList className="grid w-full grid-cols-3 gap-2">
                  <TabsTrigger value="wallet" className="flex p-2 items-center gap-2">
                    <WalletCards className="size-4" />
                    Wallet
                  </TabsTrigger>
                  <TabsTrigger value="saved-card" className="flex p-2 items-center gap-2">
                    <CreditCard className="size-4" />
                    Saved Card
                  </TabsTrigger>
                  <TabsTrigger value="new-card" className="flex p-2 items-center gap-2">
                    <CreditCard className="size-4" />
                    New Card
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="wallet" className="space-y-4">
                  <WalletPaymentOption balance={walletBalance} amount={acceptanceFee} loading={walletLoading} />
                </TabsContent>

                <TabsContent value="saved-card" className="space-y-4">
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <Label>Your Saved Cards</Label>
                      {deluxeSavedCards.map((card: any, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setSelectedDeluxeCardId(card.paymentMethodId)}
                          className={`w-full p-3 rounded-lg transition-all ${
                            selectedDeluxeCardId === card.paymentMethodId
                              ? 'border-blue-600 border-2 bg-blue-50'
                              : 'border-0 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              style={{
                                padding: "1rem",
                                background: "linear-gradient(to right, #2563eb, #1d4ed8)",
                                borderRadius: "0.5rem",
                              }}
                            >
                              <CreditCard style={{ width: "20px", height: "20px", color: "white" }} />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2">
                                <div className="font-medium">
                                  {`${card.cardType || 'Card'} ${card.maskedPan || ''}`}
                                </div>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Expires {card.expiry}
                              </div>
                            </div>
                            {selectedDeluxeCardId === card.paymentMethodId && (
                              <div className="size-5 bg-blue-600 rounded-full flex items-center justify-center">
                                <div className="size-2 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>

                    {rejectedCardReson.toLowerCase().includes("insufficient") && (
                      <Alert className="bg-orange-50 border-orange-200">
                        <AlertCircle className="size-4 text-orange-600" />
                        <AlertDescription className="text-orange-900">
                          <strong>Insufficient Funds?</strong> Add funds to your selected card before retrying the payment.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      className="lead-payment-add-card w-full outline-none"
                      onClick={() => setPaymentMethod('new-card')}
                    >
                      <Plus className="size-4 mr-2" />
                      Add New Card
                    </Button>

                    <div style={{ border:`1px solid #bfdbfe` }} className="bg-blue-50 rounded-lg px-3 pt-3">
                      <div className="text-sm text-blue-900">
                        <strong>Retry with saved card:</strong>
                      </div>
                      <ul className="text-xs text-blue-800 mt-2 space-y-1 ml-4 list-disc">
                        <li>Make sure your card has sufficient funds</li>
                        <li>Check that your card hasn't been blocked</li>
                        <li>Contact your bank if issues persist</li>
                      </ul>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="new-card" className="space-y-4">
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Card Details
                    </label>
                    <div
                      id="embeddedpayments"
                      style={{ width:"100%" }}
                      className={`${sdkRendered ? 'opacity-100' : 'opacity-0'} min-h-[160px] transition-opacity`}
                    />
                    {!sdkRendered && (
                      <div className="space-y-3 animate-pulse">
                        <div className="h-10 rounded-lg bg-gray-100" />
                        <div className="h-10 rounded-lg bg-gray-100" />
                        <div className="h-10 rounded-lg bg-gray-100" />
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-start gap-3">
                  <Lock className="size-5 text-gray-600 mt-0.5" />
                  <div className="text-xs text-muted-foreground mt-1">
                    Your payment information is encrypted and secure. We never store your full card details.
                  </div>
                </div>
              </div>

              {message && (
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className='bg-transparent border'
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    {mode === 'retry' ? <RefreshCw className="size-4 mr-2" /> : <Lock className="size-4 mr-2" />}
                    {mode === 'retry' ? 'Retry Payment' : 'Pay & Accept Lead'} - ${acceptanceFee.toFixed(2)}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StripePaymentDeclinedModal({
  lead,
  onClose,paymentProvider,
  onRetry,rejectedCardReson,setRejectedReason,
  mode = 'retry'
}: PaymentDeclinedModalProps) {
  const isDeluxePayment = String(paymentProvider || '').toLowerCase() === 'deluxe';
  const [paymentMethod, setPaymentMethod] = useState<LeadPaymentMethod>('wallet');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedSavedCard, setSelectedSavedCard] = useState<SavedCard | null>(null);
  const [selectedDeluxeCardId, setSelectedDeluxeCardId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const userDetails = useSelector(selectUserDetails)
  const currentUser = Array.isArray(userDetails) ? userDetails[0] : userDetails;
  const isContractor = String(currentUser?.user_type || currentUser?.userType || '').toLowerCase() === 'contractor';
  const [deluxeSavedCards,setDeluxeSavedCards]=useState([])
  const [refrsh,setRefresh]= useState(false)
  const [deluxeJwt, setDeluxeJwt] = useState<string | null>(null);
  const [sdkRendered, setSdkRendered] = useState(false);
  const initializedRef = useRef(false);
  const tokenSuccessCalledRef = useRef(false);
  // Calculate acceptance fee (10% of max budget or $50, whichever is greater)
  const acceptanceFee = Number(lead.tradesmens_commission) 
  const { walletBalance, walletLoading } = useWalletBalance();
  // Card payment state
 
  const [savedCard,setSavedCard]=useState([])
  const navigation = useNavigate()
  const userEmail = userDetails?.[0]?.user_email || userDetails?.user_email || '';
  const stripe = useStripe();
  const elements = useElements();

  const resetDeluxeSdk = () => {
    setSdkRendered(false);
    setDeluxeJwt(null);
    initializedRef.current = false;
    tokenSuccessCalledRef.current = false;

    const container = document.getElementById('embeddedpayments');
    if (container) container.innerHTML = '';
  };

  const handlePaymentAccepted = async ({
    paymentMethodId = '',
    deluxeToken = '',
    shouldSaveStripeCard = false,
    stripeCardElement = null,
    deluxePaymentMethodId="",expiry,
    useWallet = false
  }: {
    paymentMethodId?: string;
    deluxeToken?: string;
    shouldSaveStripeCard?: boolean;
    stripeCardElement?: any;
    deluxePaymentMethodId?:any;
    expiry?:any;
    useWallet?: boolean;
  }) => {
    const loading = toast.loading('Loading');

    try {
      await CallAPIPostPromise(`/jobs/acceptJob`,{
        projectStatus:"ACCEPTED",
        contractorId:getDecodeId(),
        jobId:Number(lead.id) ,
        category:lead.lead_sub_cat,
        ...(useWallet ? { paymentSource: 'wallet' } : { paymentSource: 'card' }),
        ...(paymentMethodId ? { paymentMethodId } : {}),
        ...(deluxePaymentMethodId ? { deluxePaymentMethodId } : {}),
        ...(deluxeToken ? { deluxePaymentToken: deluxeToken ,expiry} : {}),
      });

      toast.success('Lead Applied Successfully! 🎉', {
        description: `You've applied for "${lead.title}".`
      });
      onClose();
      navigation("/project-ongoing-leads");

      if (shouldSaveStripeCard && stripe && stripeCardElement && paymentMethodId) {
        const { token } = await stripe.createToken(stripeCardElement, {
          name: userEmail,
        });

        if (token) {
          await api.post(`/stripe/api/stripe-payment`, {
            token,
            email: userEmail,
            amount: 999,
            userId: getDecodeId(),
            setPrimary: false,
          }).catch(() => {
            console.log('card saved err');
          }).finally(() => {
            setRefresh(r => !r);
          });
        }
      }
    } catch (e:any) {
      const reason = e.reason || e.message || 'Payment could not be completed';
      setRejectedReason(reason);
      setMessage(reason);
      toast.error(e.message, {
        description: e.reason
      });
    } finally {
      setIsProcessing(false);
      toast.dismiss(loading);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (paymentMethod === 'wallet') {
      if (walletLoading) {
        setMessage('Wallet balance is still loading.');
        return;
      }
      if (walletBalance < acceptanceFee) {
        setMessage('Your wallet balance is not enough for this lead.');
        return;
      }
      setIsProcessing(true);
      await handlePaymentAccepted({ useWallet: true });
      return;
    }

    if (isDeluxePayment) {
      if (paymentMethod === 'saved-card') {
        if (!selectedDeluxeCardId) {
          setMessage('Please select a saved card to continue.');
          return;
        }

        setIsProcessing(true);
        await handlePaymentAccepted({
          deluxePaymentMethodId: selectedDeluxeCardId,
        });
        return;
      }

      if (!deluxeJwt) {
        setMessage('Card form is still loading. Please wait a moment.');
        return;
      }

      if (typeof EmbeddedPayments === 'undefined') {
        setMessage('Unable to load Deluxe payment form. Please refresh and try again.');
        return;
      }

      setIsProcessing(true);
      tokenSuccessCalledRef.current = false;
      EmbeddedPayments.pay(deluxeJwt);
      return;
    }

    if (!isDeluxePayment && (!stripe || !elements)) {
      setMessage("Stripe failed to initialize.");
      return;
    }

    let cardElement:any;
    let paymentMethodId = '';

    if(paymentMethod ==="new-card" && elements && stripe){
      cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setMessage("CardElement not found");
        return;
      }

      setIsProcessing(true);

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error || !paymentMethod) {
        setMessage(error?.message || "Failed to create payment method.");
        setIsProcessing(false);
        return;
      }
      paymentMethodId = paymentMethod.id;
    }else{
      paymentMethodId =selectedSavedCard?.id ||"";
      if (!paymentMethodId) {
        setMessage('Please select a saved card to continue.');
        return;
      }
      setIsProcessing(true);
    }

    await handlePaymentAccepted({
      paymentMethodId,
      shouldSaveStripeCard: paymentMethod === 'new-card',
      stripeCardElement: cardElement,
    });
  };

  useEffect(()=>{
    if (isDeluxePayment) {
      return;
    }

    CallAPIGetPromise(`/stripe/api/get-saved-cards/${getDecodeId()}`).then((res:any)=>{
      if(res?.data){
        setSavedCard(res.data)
        setSelectedSavedCard(res.data.find((card: any) => card.isPrimary) || res.data[0] || null)
      }
    }).catch(()=>{

    })
  
  },[refrsh, isDeluxePayment])

  useEffect(() => {
    if (!isDeluxePayment || !isContractor) {
      return;
    }
    const decodedToken = decodeJwt();
     CallAPIPostPromise(`/v2/api/deluxe/getSavedCards/`,{
      email:decodedToken?.email || "",
      userId:getDecodeId()
    }).then((resp:any)=>{
    const cards = resp?.cards || [];
   setDeluxeSavedCards(cards)
      if (paymentMethod === 'saved-card' && cards?.length) {
        setSelectedDeluxeCardId(cards[0]?.paymentMethodId || '');
      }
  
      if (!cards?.length && paymentMethod === 'saved-card') {
        setPaymentMethod('new-card');
      }
    }).catch(()=>{
      
    })
   


  
  }, [isDeluxePayment, paymentMethod, isContractor]);

  useEffect(() => {
    if (!isDeluxePayment || paymentMethod !== 'new-card') {
      return;
    }

    resetDeluxeSdk();
  }, [isDeluxePayment, paymentMethod]);

  useEffect(() => {
    if (!isDeluxePayment || paymentMethod !== 'new-card') {
      return;
    }

    const fetchJwt = async () => {
      try {
        const resp = await api.post(`/v2/api/deluxe/create-jwt`, {
          userId: getDecodeId(),
          email: userEmail,
        }) as any;

        if (!resp?.jwt) {
          throw new Error('JWT missing');
        }
        setDeluxeJwt(resp.jwt);
      } catch (error) {
        console.error('Deluxe JWT error', error);
        setMessage('Unable to initialize card form. Please try again.');
      }
    };

    fetchJwt();
  }, [isDeluxePayment, paymentMethod, userEmail]);

  useEffect(() => {
    if (!isDeluxePayment || paymentMethod !== 'new-card' || !deluxeJwt) {
      return;
    }

    if (typeof EmbeddedPayments === 'undefined') {
      setMessage('Deluxe payment SDK is not loaded yet.');
      return;
    }

    const container = document.getElementById('embeddedpayments');
    if (!container || initializedRef.current) {
      return;
    }

    initializedRef.current = true;
    tokenSuccessCalledRef.current = false;

    EmbeddedPayments.init(deluxeJwt, {
      countryCode: "US",
      currencyCode: "USD",
      paymentMethods: ["cc"],
      collectBillingAddress: false,
      collectShippingAddress: false,
      requireBillingAddress: false,
      skipSellerStatusCheck: true,
      skipCompleteAuthorization: true,
    })
      .then((instance: any) => {
        instance
          .setEventHandlers({
            onTxnSuccess: () => {},
            onTxnFailed: () => {
              setIsProcessing(false);
            },
            onTxnAuthorized: () => {},
            onValidationError: () => {
              setMessage('Please check your card details and try again.');
              setIsProcessing(false);
            },
            onCancel: () => {
              setIsProcessing(false);
            },
            onTokenSuccess: async (_gateway: string, data: any) => {
              if (tokenSuccessCalledRef.current) return;
              tokenSuccessCalledRef.current = true;

              const token = data?.Token || data?.token;
              const expiry = data?.ExpMonthYear || data?.expiry;
            
              if (!token) {
                tokenSuccessCalledRef.current = false;
                setMessage('Token not received properly. Try again.');
                setIsProcessing(false);
                return;
              }

              await handlePaymentAccepted({
                deluxeToken: token,
                expiry:expiry
              });
            },
            onTokenFailed: () => {
              tokenSuccessCalledRef.current = false;
              setMessage('Unable to tokenize card. Please try again.');
              setIsProcessing(false);
            },
          })
          .render({
            containerId: "embeddedpayments",
            paymentpanelstyle: "light",
            paybuttoncolor: "#2563eb",
            cancelbuttoncolor: "#ef4444",
          });

        setTimeout(() => {
          setSdkRendered(true);
        }, 150);
      })
      .catch((error: any) => {
        console.error('EmbeddedPayments init error:', error);
        setMessage('Unable to load card form. Please refresh and try again.');
        setSdkRendered(false);
        setIsProcessing(false);
      });
  }, [deluxeJwt, isDeluxePayment, paymentMethod]);

  useEffect(() => {
    return () => {
      resetDeluxeSdk();
    };
  }, []);
  return (
    <div className=''>
  <Dialog open={true} onOpenChange={onClose} >
      <DialogContent style={{
        maxHeight:'95%',
        maxWidth:window.innerHeight>600? 700:400
      }} className="lead-payment-dialog tailwind-prefix overflow-y-auto">

        <DialogHeader >
          <DialogTitle className={`flex items-center gap-2 ${mode === 'retry' ? 'text-red-600' : 'text-gray-950'}`}>
            {mode === 'retry' ? <AlertCircle className="size-5" /> : <Lock className="size-5 text-blue-600" />}
            {mode === 'retry' ? 'Payment Declined' : 'Choose payment method'}
          </DialogTitle>
          <div className='text-gray-500'>
            {mode === 'retry' ? rejectedCardReson : 'Select how you want to pay before accepting this lead.'}
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            {/* Decline Reason Alert */}
            {mode === 'retry' && rejectedCardReson && (
              <Alert variant="destructive" className=''>
                <AlertCircle className="size-4" />
                <AlertDescription>
                  <strong>Reason:</strong> {rejectedCardReson}
                </AlertDescription>
              </Alert>
            )}

            {/* Amount Display */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-900">Amount to Pay</span>
                <span className="text-2xl text-blue-900">${acceptanceFee.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Method Tabs */}
            <Tabs value={paymentMethod} onValueChange={(value) => setPaymentMethod(value as LeadPaymentMethod)}>
              <TabsList className="grid w-full grid-cols-3 gap-2">
                <TabsTrigger value="wallet" className="flex p-2 items-center gap-2">
                  <WalletCards className="size-4" />
                  Wallet
                </TabsTrigger>
                <TabsTrigger value="saved-card" className="flex p-2 items-center  gap-2">
                  <CreditCard className="size-4" />
                  Saved Card
                </TabsTrigger>
                <TabsTrigger value="new-card" className="flex p-2 items-center gap-2">
                  <CreditCard className="size-4" />
                  New Card
                </TabsTrigger>
              </TabsList>

              <TabsContent value="wallet" className="space-y-4">
                <WalletPaymentOption balance={walletBalance} amount={acceptanceFee} loading={walletLoading} />
              </TabsContent>

              {/* Saved Card Tab */}
              <TabsContent value="saved-card" className="space-y-4 ">
                <div className="space-y-4">
                  {/* Saved Cards List */}
                  <div className="space-y-3">
                    <Label>Your Saved Cards</Label>
                    {(isDeluxePayment ? deluxeSavedCards : savedCard).map((card:any,index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          if (isDeluxePayment) {
                            setSelectedDeluxeCardId(card.paymentMethodId);
                            return;
                          }
                          setSelectedSavedCard(card);
                        }}
                        className={`w-full p-3 rounded-lg transition-all ${
                          (
                            isDeluxePayment
                              ? selectedDeluxeCardId === card.paymentMethodId
                              : selectedSavedCard?.id === card.id
                          )
                            ? 'border-blue-600 border-2   bg-blue-50'
                            : 'border-0 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                        <div
  style={{
    padding: "1rem",
    background: "linear-gradient(to right, #2563eb, #1d4ed8)",
    borderRadius: "0.5rem",
  }}
>
  <CreditCard style={{ width: "20px", height: "20px", color: "white" }} />
</div>

                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                              <div className="font-medium">
                                {isDeluxePayment
                                  ? `${card.cardType || 'Card'} ${card.maskedPan || ''}`
                                  : `${card.card.brand} •••• ${card.card.last4}`}
                              </div>
                              {!isDeluxePayment && card.isPrimary && (
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                                  Primary
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Expires {isDeluxePayment ? card.expiry : `${card.card.exp_month}/${card.card.exp_year}`}
                            </div>
                          </div>
                          {(isDeluxePayment ? selectedDeluxeCardId === card.paymentMethodId : selectedSavedCard?.id === card.id) && (
                            <div className="size-5 bg-blue-600 rounded-full flex items-center justify-center">
                              <div className="size-2 bg-white rounded-full"></div>
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Add Funds Option */}
                  {rejectedCardReson.toLowerCase().includes("insufficient") &&  <Alert className="bg-orange-50 border-orange-200">
                    <AlertCircle className="size-4 text-orange-600" />
                    <AlertDescription className="text-orange-900">
                      <strong>Insufficient Funds?</strong> Add funds to your selected card before retrying the payment.
                    </AlertDescription>
                  </Alert>}
                 

                  {/* Add Funds Button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="lead-payment-add-card w-full outline-none"
                    onClick={() => setPaymentMethod('new-card')}
       
                  >
                    <Plus className="size-4 mr-2" />
                    Add New Card
                  </Button>

                  {/* Info about retry */}
                  <div style={{
                    border:`1px solid #bfdbfe`
                  }} className="bg-blue-50  rounded-lg px-3 pt-3  ">
                    <div className="text-sm text-blue-900">
                      <strong>Retry with saved card:</strong>
                    </div>
                    <ul className="text-xs text-blue-800 mt-2  space-y-1 ml-4 list-disc">
                      <li>Make sure your card has sufficient funds</li>
                      <li>Check that your card hasn't been blocked</li>
                      <li>Contact your bank if issues persist</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              {/* Different Card Tab */}
              <TabsContent value="new-card" className="space-y-4 ">
                <div className="space-y-4">
                  {isDeluxePayment ? (
                    <div style={{
                    
                    }} className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Card Details
                      </label>
                      <div
                        id="embeddedpayments"
                        style={{
                          width:"100%"
                        }}
                        className={`${sdkRendered ? 'opacity-100' : 'opacity-0'} min-h-[160px] transition-opacity`}
                      />
                      {!sdkRendered && (
                        <div className="space-y-3 animate-pulse">
                          <div className="h-10 rounded-lg bg-gray-100" />
                          <div className="h-10 rounded-lg bg-gray-100" />
                          <div className="h-10 rounded-lg bg-gray-100" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Card Details
                      </label>
                      <div className="p-2 border border-gray-300 rounded-lg bg-white shadow-sm">
                        <CardElement />
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Stripe Security Notice */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <Lock className="size-5 text-gray-600 mt-0.5" />
                <div>
                
                  <div className="text-xs text-muted-foreground mt-1">
                    Your payment information is encrypted and secure. We never store your full card details.
                  </div>
                </div>
              </div>
            </div>
            {message && (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className='bg-transparent border'
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={
                isProcessing ||
                (!isDeluxePayment &&
                  paymentMethod === "new-card" &&
                  (!stripe || !elements))
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  {mode === 'retry' ? <RefreshCw className="size-4 mr-2" /> : <Lock className="size-4 mr-2" />}
                  {mode === 'retry' ? 'Retry Payment' : 'Pay & Accept Lead'} - ${acceptanceFee.toFixed(2)}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      

    </Dialog>
    </div>
  
  );
}
